import { Box, Grid } from '@chakra-ui/react'
import { memo } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import type { ExtensionInstance } from '@mangovoice/types'

import type { SwitchboardApi } from '../../api/switchboardApi'
import type { SwitchboardTranslateFn } from '../../types'
import { SwitchboardCard } from './SwitchboardCard'

/** Matched switchboard with extension data */
export interface MatchedSwitchboard {
  extension: ExtensionInstance
  switchboard?: {
    extension_dial_plan: number
    updated_at: string
    json_data?: unknown
  }
}

interface SwitchboardGridItemProps {
  item: MatchedSwitchboard
  pbxId: number
  onDelete?: (id: number) => void
  canDelete?: boolean
  isDeleting: boolean
  onNavigate?: (extensionDialPlan: number) => void
  getNavigationHref?: (extensionDialPlan: number) => string
  api: SwitchboardApi
  toast?: ToastLike
  onMetadataUpdated?: () => void
  t?: SwitchboardTranslateFn
}

const SwitchboardGridItem = memo<SwitchboardGridItemProps>(
  ({
    item,
    pbxId,
    onDelete,
    canDelete = true,
    isDeleting,
    onNavigate,
    getNavigationHref,
    api,
    toast,
    onMetadataUpdated,
    t,
  }) => {
    const { switchboard: sb, extension } = item

    if (!extension) return null

    const extensionDialPlanId = extension.id

    const switchboardData = {
      extension_dial_plan: extensionDialPlanId,
      updated_at:
        sb?.updated_at || extension.updated_at || new Date().toISOString(),
    }

    return (
      <SwitchboardCard
        switchboard={switchboardData}
        extension={extension}
        pbxId={pbxId}
        onDelete={onDelete}
        canDelete={canDelete}
        isDeleting={isDeleting}
        onNavigate={onNavigate}
        navigationHref={getNavigationHref?.(extensionDialPlanId)}
        api={api}
        toast={toast}
        onMetadataUpdated={onMetadataUpdated}
        t={t}
      />
    )
  },
)

SwitchboardGridItem.displayName = 'SwitchboardGridItem'

export interface SwitchboardGridProps {
  /** Array of matched switchboards to display */
  switchboards: MatchedSwitchboard[]
  /** PBX ID */
  pbxId: number
  /** Callback when delete is triggered */
  onDelete?: (id: number) => void
  /** Whether user can delete switchboards */
  canDelete?: boolean
  /** Whether delete operation is in progress */
  isDeleting: boolean
  /** Callback when card is clicked for navigation */
  onNavigate?: (extensionDialPlan: number) => void
  /** Function to generate a native browser navigation route */
  getNavigationHref?: (extensionDialPlan: number) => string
  /** Switchboard API instance */
  api: SwitchboardApi
  /** Optional toast function for notifications */
  toast?: ToastLike
  /** Callback after successful metadata update */
  onMetadataUpdated?: () => void
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

/**
 * Responsive grid layout for switchboard cards
 * 1 column <950px, 2 columns 950-1249px, 3 columns ≥1250px
 */
export const SwitchboardGrid = memo<SwitchboardGridProps>(
  ({
    switchboards,
    pbxId,
    onDelete,
    canDelete = true,
    isDeleting,
    onNavigate,
    getNavigationHref,
    api,
    toast,
    onMetadataUpdated,
    t,
  }) => {
    return (
      <Box
        display="flex"
        px={6}
        py={0.5}
        flexDirection="column"
        alignItems="center"
        gap="24px"
        flex="1 0 0"
        alignSelf="stretch"
        w="100%"
      >
        <Grid
          gap="24px"
          w="100%"
          css={{
            gridTemplateColumns: '1fr',
            '@media (min-width: 950px)': {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
            '@media (min-width: 1250px)': {
              gridTemplateColumns: 'repeat(3, 1fr)',
            },
          }}
        >
          {switchboards.map((item) => (
            <SwitchboardGridItem
              key={item.extension?.id}
              item={item}
              pbxId={pbxId}
              onDelete={onDelete}
              canDelete={canDelete}
              isDeleting={isDeleting}
              onNavigate={onNavigate}
              getNavigationHref={getNavigationHref}
              api={api}
              toast={toast}
              onMetadataUpdated={onMetadataUpdated}
              t={t}
            />
          ))}
        </Grid>
      </Box>
    )
  },
)

SwitchboardGrid.displayName = 'SwitchboardGrid'
